/**
 * @file lib/seoLocations.js
 * @description Where ABAcases has public location pages (page engine, 2026-10-20). One engine for every list of
 *   location links (the "ABA jobs by state" index, the homepage's other-states group, the footer), so a new state
 *   appears everywhere on the week it is released and never before. Built from the released landings
 *   (data/seoLanding, already filtered by data/seoRelease) and the states table in data/seoAreas.json (names and
 *   the BACB workforce rank that orders them).
 *
 *   Link volume stays bounded by design: the footer carries one link (the index), the index lists states, a state
 *   lists its places, and a city page links at most 6 related pages (relatedLandings).
 * @module lib/seoLocations
 */
import { SEO_LANDINGS } from "../data/seoLanding";
import seoAreas from "../data/seoAreas.json";

/** The page a place is linked by: its RBT page when it has one (the most searched role), else its first page. */
function primaryPage(pages) {
  return pages.find((p) => p.roleKey === "rbt") || pages[0];
}

/**
 * Released states, highest ABA workforce first (BACB rank; states not in the table go last, by name).
 * @returns {Array<{ code, name, nameEs, rank, hub, places: Array<{ areaKey, area, nameEs, areaType, page, pages }> }>}
 *   hub = the state's own page (areaType "state"), or null; places = its cities, counties and regions.
 */
export function liveStates(landings = SEO_LANDINGS, states = seoAreas.states) {
  const byState = new Map();
  for (const e of landings) {
    if (!byState.has(e.state)) byState.set(e.state, new Map());
    const places = byState.get(e.state);
    if (!places.has(e.areaKey)) places.set(e.areaKey, []);
    places.get(e.areaKey).push(e);
  }
  const out = [];
  for (const [code, places] of byState) {
    const meta = states[code] || {};
    let hub = null;
    const list = [];
    for (const [areaKey, pages] of places) {
      const first = pages[0];
      const item = { areaKey, area: first.area, nameEs: first.nameEs || null, areaType: first.areaType, page: primaryPage(pages), pages };
      if (first.areaType === "state") hub = item;
      else list.push(item);
    }
    // Regions (South Florida) before cities and counties; then by name.
    list.sort((a, b) => (a.areaType === "region" ? 0 : 1) - (b.areaType === "region" ? 0 : 1) || a.area.localeCompare(b.area));
    out.push({ code, name: meta.name || code, nameEs: meta.nameEs || meta.name || code, rank: meta.rank || 999, hub, places: list });
  }
  return out.sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
}

/** States other than the ones a page already features by hand (e.g. the homepage's Florida and NC groups). */
export function otherLiveStates(exclude = [], landings, states) {
  const skip = new Set(exclude);
  return liveStates(landings, states).filter((s) => !skip.has(s.code));
}

export const LOCATIONS_INDEX_SLUG = "aba-jobs-by-state";
